import React, { useState, useCallback, useMemo, useEffect } from 'react';
import classNames from 'classnames';
import { AssigneeDto } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/Assignments/AssigneeDto';
import { Assignee } from './Assignee';
import { searchWithExclusion, getByStates } from './utils/AssignmentUtils';
import './AssignmentField.less';
import { SearchBox, PagingSearchResult } from 'components/inputs/SearchBox';
import { State } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/Assignments/State';
import { isNotEmpty } from 'utils/ArrayUtils';

export interface IAssignmentFieldProps extends Omit<React.HTMLProps<HTMLDivElement>, 'onChange'> {
  assignees: AssigneeDto[];
  placeholder?: string; // pass this in for i18n
  inputClassName?: string;
  search: (text: string, loadedCount: number, pageSize: number) => Promise<PagingSearchResult<AssigneeDto>>;
  onChange: (assignees: AssigneeDto[]) => void;
  layerTestId?: string;
}

const getInputClasses = (assignees: AssigneeDto[]) =>
  classNames(`dpc-assignees__field`, {
    'dpc-assignees__field--alone': !isNotEmpty(assignees),
    'dpc-assignees__field--accompanied': isNotEmpty(assignees),
  });

function getAddedIds(assignees: AssigneeDto[]): string[] {
  return getByStates(assignees, State.Added).map(item => item.Id);
}

/**
 * Assignee list and search (TODO) field.
 *
 * Copyright (c) 2019 Decisions All Rights Reserved
 */
export function AssignmentField({
  assignees,
  placeholder,
  search: _search,
  onChange,
  layerTestId,
  ...htmlOptions
}: IAssignmentFieldProps) {
  const [selectedAssignees, setAssignees] = useState(assignees);
  const [newIds, setNew] = useState<string[]>(getAddedIds(assignees));
  const [searchText, setSearchText] = useState('');

  // force to update state if properties were changed
  useEffect(() => {
    setAssignees(assignees);
    setNew(getAddedIds(assignees));
  }, [assignees]);

  const searchFn = useMemo(() => searchWithExclusion(_search, selectedAssignees), [_search, selectedAssignees]);

  const onChosen = useCallback(
    (assignee: AssigneeDto) => {
      let newItem = { ...assignee };
      // To be sure we will not add the same item twice
      const isNew = selectedAssignees.map(item => item.Id).indexOf(newItem.Id) === -1;
      setSearchText('');

      if (isNew) {
        // We don't want to override other states.
        // There are items with "Excluded" state and we should not change state for them.
        if (newItem.State === State.Initial) {
          newItem.State = State.Added;
        }

        const newAssignees = [...selectedAssignees, newItem];
        setAssignees(newAssignees);
        setNew([...newIds, assignee.Id]);
        onChange && onChange(newAssignees);
      }
    },
    [newIds, onChange, selectedAssignees]
  );

  const onRestore = useCallback(
    (isDeleted: boolean, assignee: AssigneeDto) => {
      const assignees = [...selectedAssignees];
      const restoredAssignee = assignees.find(item => item.Id === assignee.Id);

      if (restoredAssignee) {
        restoredAssignee.State = isDeleted ? State.Removed : newIds.includes(assignee.Id) ? State.Added : State.Initial;
      }

      setAssignees(assignees);
      onChange && onChange(assignees);
    },
    [newIds, onChange, selectedAssignees]
  );

  return (
    <div className="dpc-assignees" {...htmlOptions}>
      <ul className="dpc-assignees__list">
        {selectedAssignees.map(assignee => (
          <Assignee key={assignee.Id} assignee={assignee} onRestore={onRestore} new={newIds.includes(assignee.Id)} />
        ))}
      </ul>
      <SearchBox
        search={{
          searchFn,
          pageSize: 30,
        }}
        inputProps={{
          searchText,
          setSearchText,
        }}
        showSearchIcon={false}
        className={getInputClasses(selectedAssignees)}
        placeholder={'Add Assignee'}
        itemTemplate={({ item, isFocused, chooseItemCallback }) => (
          <Assignee
            key={item.Id}
            assignee={item}
            canRestore={false}
            isSuggestion
            onClick={chooseItemCallback}
            isFocused={isFocused}
          />
        )}
        onItemChosen={onChosen}
        layerProps={{
          layerTestId,
        }}
        doNotEmitOnBlur={true}
      />
    </div>
  );
}
